// @flow

/*  orderForm actions
 *  @desc These actions belong to the order form, and are used for fetching an address,
 *  stock validation, order processing, and the submission of the form to our LOA.
 */

import {
  formValueSelector,
  change,
  touch,
} from 'redux-form';
import { push } from 'connected-react-router';
import { fetchData } from '../../helpers';
import { getConfirmationLink } from '../pdb';
import {
  getActiveStockId,
  getActiveColorId,
} from '../configuration/actives/selectors';
import { getStockResultStatus } from './selectors';
import logger from '../../helpers/logger';
import { processConfiguration } from '../configuration';
import {
  onPurchase,
  addCriteoTransactionPage,
} from '../gtm';
import {
  INIT_FORM,
  INIT_CHECK_STOCK,
  RECEIVED_CHECK_STOCK,
  CHECK_STOCK_ERROR,
  OUT_OF_STOCK,
  ALTERNATIVES_OVERLAY_CLOSE,
  FORM_SUBMIT_START,
  FORM_SUBMIT_COMPLETE,
} from './constants';

const selector = formValueSelector('order');

/*  fetchAddress action
 *  @desc This action is used for fetching an address that belongs to a zipcode / housenumber
 *  combination.
 */
export const fetchAddress = (
  fieldName: string,
): Function => async (dispatch, getState) => {
  const state = getState();
  const formName = 'order';
  const zipcode = selector(state, `${fieldName}.postal_code`);
  const housenumber = selector(state, `${fieldName}.house_number`);

  if (!zipcode || !housenumber) {
    return;
  }

  const streetName = `${fieldName}.street`;
  const cityName = `${fieldName}.city`;

  try {
    const autoCompleteFields = await fetchData({
      endpoint: '/api/check-postalcode',
      method: 'POST',
      body: {
        zipcode,
        housenumber,
      },
    });

    dispatch(change(formName, cityName, autoCompleteFields.json.city));
    dispatch(touch(formName, cityName));
    dispatch(change(formName, streetName, autoCompleteFields.json.street));
    dispatch(touch(formName, streetName));
  } catch (error) {
    console.error('An error occurred while trying to fetch the zipcode', error);
  }
};

/*  initForm action
 *  @desc This action is used to reset the formData store to its initial values each time the user
 *  revisits the order form.
 */
export const initForm = () => ({
  type: INIT_FORM,
});

/*  initForm action
 *  @desc This action is used to indicate that the call to the checkStock endpoint has started.
 */
const initCheckStock = () => ({
  type: INIT_CHECK_STOCK,
});

/*  receivedCheckStock action
 *  @desc This action is used to indicate that the call to the checkStock endpoint has returned
 *  results.
 *  @param stockResult - The result that is received from the checkStock endpoint.
 */
const receivedCheckStock = (stockResult: Object) => ({
  type: RECEIVED_CHECK_STOCK,
  payload: stockResult,
});

/*  checkStockError action
 *  @desc This action is used to indicate that the call to the checkStock endpoint has resulted in
 *  an error.
 *  @param error - The error that occurred when trying to call the checkStock endpoint.
 */
const checkStockError = error => ({
  type: CHECK_STOCK_ERROR,
  payload: error,
});

/*  formSubmitStart action
 *  @desc This action is used to indicate that the actual order submission has started.
 */
const formSubmitStart = () => ({
  type: FORM_SUBMIT_START,
});

/*  formSubmitComplete action
 *  @desc This action is used to indicate that the actual order submission has been completed.
 */
const formSubmitComplete = () => ({
  type: FORM_SUBMIT_COMPLETE,
});

/*  outOfStock action
 *  @desc This action is dispatched when the an item is out of stock (the call to check stock
 *  returns false).
 *  @param stockId - The stockId of the item that is out of stock.
 */
const outOfStock = stockId => ({
  type: OUT_OF_STOCK,
  payload: stockId,
});

/*  alternativesOverlayClose action
 *  @desc This action is used to close the alternatives overlay.
 */
export const alternativesOverlayClose = () => ({
  type: ALTERNATIVES_OVERLAY_CLOSE,
});

/*  submitForm action
 *  @desc This action is used to submit the order form.
 *  @param values - The form values that need to be submitted to the back-end.
 */
export const submitForm = (values: Object): Function => async (dispatch, getState) => {
  const state = getState();
  const confirmationLink = getConfirmationLink(state);
  dispatch(formSubmitStart());
  const {
    data: { order_number },
  } = await dispatch(processConfiguration({ personalData: values }));
  dispatch(push(confirmationLink));
  dispatch(addCriteoTransactionPage(order_number));
  dispatch(onPurchase(order_number));
  dispatch(formSubmitComplete());
};

/*  validateStockBeforeSubmission action
 *  @desc This action is used validate the form before it is submitted. We check if the car is out
 *  of stock first.
 *  @param values - The form values that need to be submitted to the back-end.
 */
const validateStockBeforeSubmission = (values: Object): Function => async (dispatch, getState) => {
  const state = getState();
  if (!getStockResultStatus(state)) {
    dispatch(outOfStock(getActiveColorId(state) || null));
    return;
  }
  dispatch(submitForm(values));
};

/*  checkStock action
 *  @desc This action is used make a call to the checkStock endpoint in order to validate if
 *  a car is in stock. This call has two possible results: { status: false } or
 *  { status: true; id: [STOCKID]}.
 *  @param values - The form values that need to be submitted to the back-end.
 */
export const checkStock = (values: Object): Function => async (dispatch, getState) => {
  dispatch(initCheckStock());
  const activeStockId = getActiveStockId(getState());

  // If there is no active stockId, we can just submit the form.
  if (activeStockId < 0) {
    dispatch(submitForm(values));
    return;
  }

  let response = {};
  try {
    const { json } = await fetchData({
      endpoint: `/api/check-stock/${activeStockId}`,
    });
    response = json;
  } catch (error) {
    const logMessage = 'An error occurred while trying to check the stock.';
    console.error(logMessage, error);
    logger({
      message: logMessage,
      path: window.location.pathname,
      error: error.message,
      stack: error.stack,
      level: 'emergency',
    });
    dispatch(checkStockError(error));
  }
  dispatch(receivedCheckStock(response));
  dispatch(validateStockBeforeSubmission(values));
};
